import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Select, DatePicker, Space, Form, message, Spin, List, Tag } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { getDynamicFilterOptions } from '../../../core/api/dashboard';
import { fetchBrandsBySelectedClients } from '../../../core/api/settings';
import { 
  syncSublyticsOrders, 
  syncWordPressOrders, 
  syncTransactions, 
  syncStripeTransactions, 
  syncNMITransactions, 
  syncTigerTechSales 
} from '../../../core/api/data';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DataFilters = ({ 
  filters,
  setFilters,
  onApplyFilters,
  timezone,
  setTimezone
}) => {
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState({});
  const selectedClients = useSelector((state) => state.selectedClients);
  const [loadingFilter, setLoadingFilter] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  
  // Set default date range to last 7 days when component mounts
  useEffect(() => {
    if (!filters.dateRange) {
      const endDate = dayjs();
      const startDate = dayjs().subtract(7, 'days');
      handleDateRangeChange([startDate, endDate]);
    }
  }, []);
  
  // Fetch brands when selected clients change
  useEffect(() => {
    const fetchBrands = async () => {
      if (selectedClients && selectedClients.length > 0) {
        try {
          setLoadingFilter('brands');
          
          // Pass the entire selectedClients array to the backend
          const response = await fetchBrandsBySelectedClients(selectedClients);
          
          if (response && response.data) {
            // Transform the data to the format expected by the Select component
            const brandOptions = response.data.map(brand => ({
              label: brand.brand_name,
              value: brand.id
            }));
            setBrandOptions(brandOptions);
            if (brandOptions.length ==1) {
              handleFilterChange('brands', [brandOptions[0].value]);
            }
          }
        } catch (error) {
          console.error('Error fetching brands:', error);
          message.error('Failed to load brand options');
        } finally {
          setLoadingFilter(null);
        }
      }
    };
    
    fetchBrands();
  }, [selectedClients]);

  // Fetch product options when brands change
  useEffect(() => {
    const fetchProducts = async () => {
      if (filters.brands && filters.brands.length > 0) {
        try {
          setLoadingFilter('products');
               
          
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setLoadingFilter(null);
        }
      }
    };
    
    fetchProducts();
  }, [filters.brands]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      console.log('Original date range:', dates[0].format('YYYY-MM-DD HH:mm:ss.SSS'), 'to', dates[1].format('YYYY-MM-DD HH:mm:ss.SSS'));
      
      // Always set explicit time components regardless of whether it's the same day or different days
      // This ensures consistent behavior with backend filtering
      const startDate = dates[0].hour(0).minute(0).second(0).millisecond(0);
      const endDate = dates[1].hour(23).minute(59).second(59).millisecond(999);
      dates = [startDate, endDate];
      
      console.log('Adjusted date range with explicit time components:', 
                startDate.format('YYYY-MM-DD HH:mm:ss.SSS'), 'to', 
                endDate.format('YYYY-MM-DD HH:mm:ss.SSS'));
    }
    
    // Store the dayjs objects directly for proper date handling
    setFilters(prev => ({
      ...prev,
      dateRange: dates
    }));
    
    if (dates && dates.length === 2) {
      console.log('Final date range set in filters:', 
                dates[0].format('YYYY-MM-DD HH:mm:ss.SSS'), 'to', 
                dates[1].format('YYYY-MM-DD HH:mm:ss.SSS'));
    } else {
      console.log('Date range cleared');
    }
  };



  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      brands: [],
      dateRange: null
    });
  };
  
  // Handle sync all data sources
  const handleSyncAll = async () => {
    if (!filters.brands || filters.brands.length === 0) {
      message.error('Please select at least one brand before syncing');
      return;
    }
    
    const brandId = filters.brands[0];
    setSyncing(true);
    setSyncStatus({
      sublytics: { status: 'pending', message: 'Pending...' },
      wordpress: { status: 'pending', message: 'Pending...' },
      tigertech: { status: 'pending', message: 'Pending...' },
      socrat: { status: 'pending', message: 'Pending...' },
      authnet: { status: 'pending', message: 'Pending...' },
      stripe: { status: 'pending', message: 'Pending...' },
      nmi: { status: 'pending', message: 'Pending...' }
    });
    
    // Helper function to update status for a specific data source
    const updateStatus = (source, status, message) => {
      setSyncStatus(prev => ({
        ...prev,
        [source]: { status, message }
      }));
    };
    
    // Sync Sublytics Orders
    updateStatus('sublytics', 'loading', 'Syncing Sublytics orders...');
    syncSublyticsOrders(brandId)
      .then(() => updateStatus('sublytics', 'success', 'Sublytics orders synced successfully'))
      .catch(error => updateStatus('sublytics', 'error', `Error: ${error.message || 'Failed to sync Sublytics orders'}`));
    
    // Sync WordPress Orders
    updateStatus('wordpress', 'loading', 'Syncing WordPress orders...');
    syncWordPressOrders(brandId)
      .then(() => updateStatus('wordpress', 'success', 'WordPress orders synced successfully'))
      .catch(error => updateStatus('wordpress', 'error', `Error: ${error.message || 'Failed to sync WordPress orders'}`));
    
    // Sync TigerTech Orders
    updateStatus('tigertech', 'loading', 'Syncing TigerTech orders...');
    syncTigerTechSales(brandId)
      .then(() => updateStatus('tigertech', 'success', 'TigerTech orders synced successfully'))
      .catch(error => updateStatus('tigertech', 'error', `Error: ${error.message || 'Failed to sync TigerTech orders'}`));
    
    // Sync Socrat Orders (using TigerTech with source=Socrat)
    updateStatus('socrat', 'loading', 'Syncing Socrat orders...');
    syncTigerTechSales(brandId, 'Socrat')
      .then(() => updateStatus('socrat', 'success', 'Socrat orders synced successfully'))
      .catch(error => updateStatus('socrat', 'error', `Error: ${error.message || 'Failed to sync Socrat orders'}`));
    
    // Sync Authorize.net Transactions
    updateStatus('authnet', 'loading', 'Syncing Authorize.net transactions...');
    syncTransactions(brandId)
      .then(() => updateStatus('authnet', 'success', 'Authorize.net transactions synced successfully'))
      .catch(error => updateStatus('authnet', 'error', `Error: ${error.message || 'Failed to sync Authorize.net transactions'}`));
    
    // Sync Stripe Transactions
    updateStatus('stripe', 'loading', 'Syncing Stripe transactions...');
    syncStripeTransactions(brandId)
      .then(() => updateStatus('stripe', 'success', 'Stripe transactions synced successfully'))
      .catch(error => updateStatus('stripe', 'error', `Error: ${error.message || 'Failed to sync Stripe transactions'}`));
    
    // Sync NMI Transactions
    updateStatus('nmi', 'loading', 'Syncing NMI transactions...');
    syncNMITransactions(brandId)
      .then(() => updateStatus('nmi', 'success', 'NMI transactions synced successfully'))
      .catch(error => updateStatus('nmi', 'error', `Error: ${error.message || 'Failed to sync NMI transactions'}`));
    
    // Check if all syncs are complete
    const checkAllComplete = () => {
      const allStatuses = Object.values(syncStatus);
      const pending = allStatuses.some(item => item.status === 'pending' || item.status === 'loading');
      
      if (!pending) {
        setSyncing(false);
        message.success('All sync operations completed');
        return;
      }
      
      // Check again after a delay
      setTimeout(checkAllComplete, 1000);
    };
    
    // Start checking for completion
    setTimeout(checkAllComplete, 2000);
  };

  return (
    <div className="filter-panel mt-2">
      <Form layout="vertical">
        {/* Brand Filter */}
        <Form.Item label="Brand" className="mb-3">
          <Select
            mode="multiple"
            placeholder="Select brands"
            value={filters.brands}
            onChange={(value) => handleFilterChange('brands', value)}
            loading={loadingFilter === 'brands'}
            style={{ width: '100%' }}
            allowClear
          >
            {brandOptions.map(brand => (
              <Option key={brand.value} value={brand.value}>{brand.label}</Option>
            ))}
          </Select>
        </Form.Item>

        {/* Timezone Selector */}
        <Form.Item label="Timezone" className="mb-3">
          <Select
            value={timezone}
            onChange={setTimezone}
            style={{ width: '100%' }}
          >
            <Option value="UTC">UTC</Option>
            <Option value="Asia/Dubai">Dubai (Asia/Dubai)</Option>
            <Option value="Europe/London">London (Europe/London)</Option>
            <Option value="America/New_York">New York (America/New_York)</Option>
          </Select>
        </Form.Item>



        {/* Date Range Filter */}
        <Form.Item label="Date Range" className="mb-3">
          <RangePicker 
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={handleDateRangeChange}
          />
        </Form.Item>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mt-4">
          <Button 
            type="primary" 
            onClick={onApplyFilters}
          >
            Apply Filters
          </Button>
          <Button 
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>
        
        {/* Sync All Button */}
        <div className="mt-3">
          <Button 
            type="primary"
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSyncAll}
            loading={syncing}
            disabled={!filters.brands || filters.brands.length === 0}
            block
          >
            Sync All Data
          </Button>
          
          {/* Sync Status List */}
          {syncing || Object.keys(syncStatus).length > 0 ? (
            <div className="mt-3">
              <List
                size="small"
                bordered
                dataSource={[
                  { key: 'sublytics', name: 'Sublytics Orders', ...syncStatus.sublytics },
                  { key: 'wordpress', name: 'WordPress Orders', ...syncStatus.wordpress },
                  { key: 'tigertech', name: 'TigerTech Orders', ...syncStatus.tigertech },
                  { key: 'socrat', name: 'Socrat Orders', ...syncStatus.socrat },
                  { key: 'authnet', name: 'Authorize.net Transactions', ...syncStatus.authnet },
                  { key: 'stripe', name: 'Stripe Transactions', ...syncStatus.stripe },
                  { key: 'nmi', name: 'NMI Transactions', ...syncStatus.nmi }
                ].filter(item => item.status)}
                renderItem={item => (
                  <List.Item>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <span>{item.name}</span>
                      {item.status === 'pending' && <Tag color="default">Pending</Tag>}
                      {item.status === 'loading' && <Tag color="processing" icon={<SyncOutlined spin />}>Syncing</Tag>}
                      {item.status === 'success' && <Tag color="success">Success</Tag>}
                      {item.status === 'error' && <Tag color="error">Error</Tag>}
                    </div>
                  </List.Item>
                )}
              />
            </div>
          ) : null}
        </div>
      </Form>
    </div>
  );
};

export default DataFilters;
